import { useQuery } from '@/hooks/useQuery'
import Link from '@/update-monitor/Link'
import { NextRouter, useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { Loading } from '@/components/Loading'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { str, useQueryParams } from '@/hooks/useQueryParams'
import { maybeGraphQLType } from '@/utils/graphql'
import { cn } from '@/utils/tw-helpers'
import { graphql } from 'gql.tada'
import { ChallengeContestSourceSurveyModal } from '../modal/ChallengeContestSourceSurveyModal'
import { SchoolCodeRegistration } from '../web-school-code-registration'
import { AccountSettings, BasicInfo_viewer } from './AccountSettings'
import {
  AddEmptyPlayerButton,
  AddEmptyPlayerButton_viewer,
} from './AddEmptyPlayerButton'
import { AppDownload } from './AppDownload'
import { ChallengeBanner, ChallengeBanner_challenge } from './ChallengeBanner'
import {
  CurriculumLicenseList,
  CurriculumLicenseList_viewer,
} from './CurriculumLicenseList'
import {
  CurriculumPlayerList,
  CurriculumPlayerList_viewer,
} from './CurriculumPlayerList'
import { HelpCenter } from './HelpCenter'
import { LogiqServiceBanner } from './LogiqServiceBanner'
import { PaymentInfo_viewer, PaymentSettings } from './PaymentSettings'
import { PlanInfo_viewer, PlanSettings } from './PlanSettings'

import UserIcon from '@/assets/icons/ic_logic-web_account.svg'
import AppDownloadIcon from '@/assets/icons/ic_logic-web_appDL.svg'
import HelpCenterIcon from '@/assets/icons/ic_logic-web_helpcenter.svg'
import LicenseIcon from '@/assets/icons/ic_logic-web_licensecode.svg'
import PaymentIcon from '@/assets/icons/ic_logic-web_payment.svg'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { TutorialStep } from '../TutorialStep'
import { MembershipQuestionnaireModal } from '../modal'

enum EModal {
  None,
  ChallengeContestCourseSurvey,
  MembershipQuestionnaire,
}

export const WebUserSettingsTab = (): JSX.Element => {
  const router: NextRouter & { query: { refresh?: string } } = useRouter()
  const [
    { tab, challengeSignUpSuccess, challengeId, signUpSuccess },
    updateQueryParams,
  ] = useQueryParams({
    tab: str('account'),
    challengeSignUpSuccess: str(undefined),
    challengeId: str(undefined),
    signUpSuccess: str(undefined),
  })

  const [modal, setModal] = useState<EModal>(EModal.None)

  const [{ data }] = useQuery({
    query: WebUserSetting_query,
    requestPolicy: router.query.refresh == 'true' ? 'network-only' : undefined,
  })

  const viewer = maybeGraphQLType('User')(data?.currentUser)
  const publishedNowChallenges = data?.activeChallenges.filter(
    (challenge) => challenge.publishedNow
  )
  const firstActiveChallengeId = publishedNowChallenges[0]?.id
  const hasChallengePlayers = publishedNowChallenges.some(
    (challenge) => challenge?.hasChallengePlayers
  )
  const challengeRegistrationAvailable = publishedNowChallenges.some(
    (challenge) => challenge?.registrationAvailable
  )

  const isChallengeUserWithoutPlan =
    viewer?.isChallengeUserOnly &&
    !viewer?.hasSubscription &&
    !viewer?.hasCurriculumLicense

  const tabs =
    [
        {
          value: 'account',
          label: 'アカウント設定',
          icon: <UserIcon className="w-[36px] md:w-[60px] lg:w-[78px]" />,
        },
        {
          value: 'payment',
          label: 'サブスクリプション',
          icon: <PaymentIcon className="w-[36px] md:w-[60px] lg:w-[78px]" />,
        },
        {
          value: 'license',
          label: 'ライセンス',
          icon: <LicenseIcon className="w-[36px] md:w-[60px] lg:w-[78px]" />,
        },
        {
          value: 'app-download',
          label: 'ご利用の流れ',
          icon: (
            <AppDownloadIcon className="w-[36px] md:w-[60px] lg:w-[78px]" />
          ),
        },
        {
          value: 'help',
          label: 'サポート',
          icon: <HelpCenterIcon className="w-[36px] md:w-[60px] lg:w-[78px]" />,
        },
      ]

  const [showTutorial, setShowTutorial] = useState(false)
  const isMobile = useIsMobile()

  useEffect(() => {
    if (challengeSignUpSuccess === 'true' && challengeId) {
      setModal(EModal.ChallengeContestCourseSurvey)
    }
  }, [router, challengeSignUpSuccess, challengeId])

  useEffect(() => {
    const isInUserChallengeTutorial = viewer?.notCompletedTutorials?.some(
      (tutorial) => tutorial.kind == 'ONBOARD_CHALLENGE_USER'
    )
    const isMembershipQuestionnaire = viewer?.notCompletedTutorials?.some(
      (tutorial) => tutorial.kind == 'MEMBERSHIP_QUESTIONNAIRE'
    )

    if (isInUserChallengeTutorial) {
      setShowTutorial(true)
    } else if (signUpSuccess === 'true' && isMembershipQuestionnaire) {
      setModal(EModal.MembershipQuestionnaire)
    }
  }, [viewer?.notCompletedTutorials, signUpSuccess])

  if (!data)
    return (
      <div className="pointer-events-none fixed inset-0 grid place-items-center">
        <Loading />
      </div>
    )

  return (
    <>
      <Tabs
        defaultValue={tab ?? 'account'}
        value={tab}
        onValueChange={(value) => {
          updateQueryParams({ tab: value })
        }}
      >
        <div className="relative bg-white pb-4 pt-3.5 md:pb-[26px] md:pt-5 lg:h-[280px] lg:pb-11 lg:pt-7">
          <div className="relative mx-5 max-w-[1200px] lg:mx-auto">
            <h3 className="mb-3 text-center text-sm font-bold md:mb-5 md:text-2xl lg:mb-7 lg:text-[28px]">
              アカウント
            </h3>
          </div>

          <TabsList className="flex justify-center gap-2 md:gap-3 lg:gap-3.5">
            {tabs.map((item, index) => (
              <TabsTrigger
                key={index}
                value={item.value}
                className={cn(
                  'flex h-[61px] w-[63px] flex-col items-center justify-start gap-0 rounded-lg bg-basic-bg pb-0.5 pt-1 text-[10px] font-bold leading-none text-primary-100 shadow-tiny',
                  'md:h-[100px] md:w-[130px] md:justify-center md:gap-1.5 md:pb-0 md:pt-0 md:text-xs lg:h-[138px] lg:w-[170px] lg:text-base',
                  'data-[state=active]:bg-primary-100 data-[state=active]:text-white',
                  'transition-colors'
                )}
              >
                {item.icon}
                <span
                  className={cn(
                    'flex flex-grow items-center whitespace-break-spaces md:inline md:flex-grow-0',
                    item.value === 'help' && 'px-[1px]'
                  )}
                >
                  {item.label}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {(challengeRegistrationAvailable || hasChallengePlayers) && (
          <TutorialStep
            isOpen={showTutorial && modal == EModal.None}
            onRequestClose={() => setShowTutorial(false)}
            paddingY={8}
            messageOffset={8}
            messageSide="bottom"
            message={[
              '思考力チャレンジにエントリーするには、',
              `「こちら」を${isMobile ? 'タップ' : 'クリック'}してみましょう。`,
            ]}
          >
            <ChallengeBanner challenges={publishedNowChallenges} />
          </TutorialStep>
        )}

        <TabsContent value="account">
          {!viewer?.isChallengeUserOnly && (
            <header className="bg-[#FDF0C5] py-3 text-xs md:py-4 md:text-[15px] lg:py-5 lg:text-base">
              <p className="container text-center font-bold">
                <Link
                  href="/web/user-setting/?tab=app-download"
                  className="text-primary-100"
                >
                  保護者アプリ
                </Link>
                と
                <Link
                  href="/web/user-setting/?tab=app-download"
                  className="text-primary-100"
                >
                  学習アプリ
                </Link>
                をインストールして学習をはじめましょう。
              </p>
            </header>
          )}
          <div className="px-5 pb-[30px] pt-5 md:pb-[80px] md:pt-[30px] lg:px-0 lg:pb-[100px] lg:pt-[54px]">
            <AccountSettings viewer={viewer} />
            <SchoolCodeRegistration />

            {viewer?.isChallengeUserOnly && !!firstActiveChallengeId && (
              <div className="mt-[74px] flex justify-center">
                <LogiqServiceBanner
                  challengeId={firstActiveChallengeId}
                  isChallengeUserWithoutPlan={isChallengeUserWithoutPlan}
                />
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="payment">
          {viewer?.gotPaymentProblem && (
            <div className="bg-[#FDEEE8] py-3 text-xs text-[#EC5519] md:py-4 md:text-[15px] lg:py-5 lg:text-base">
              <p className="container text-center font-bold">
                有効な支払い方法が設定されていません。
                支払い方法の設定をご確認ください。
              </p>
            </div>
          )}
          <div className="px-5 pb-[30px] pt-5 md:pb-[80px] md:pt-[30px] lg:px-0 lg:pb-[100px] lg:pt-[54px]">
            <PaymentSettings viewer={viewer} />
            <PlanSettings viewer={viewer} />
          </div>
        </TabsContent>
        <TabsContent
          value="license"
          className="px-5 pb-[30px] pt-5 md:pb-[80px] md:pt-[30px] lg:px-0 lg:pb-[100px] lg:pt-[54px]"
        >
          <CurriculumLicenseList viewer={viewer} />
          <CurriculumPlayerList viewer={viewer} />
          <AddEmptyPlayerButton viewer={viewer} />
        </TabsContent>
        <TabsContent
          value="app-download"
          className="px-5 pb-[30px] pt-5 md:pb-[80px] md:pt-[30px] lg:px-0 lg:pb-[100px] lg:pt-[54px]"
        >
          <AppDownload />
        </TabsContent>
        <TabsContent
          value="help"
          className="px-5 pb-[30px] pt-5 md:pb-[80px] md:pt-[30px] lg:px-0 lg:pb-[100px] lg:pt-[54px]"
        >
          <HelpCenter />
        </TabsContent>
      </Tabs>

      {challengeSignUpSuccess === 'true' && challengeId && (
        <ChallengeContestSourceSurveyModal
          isOpen={modal === EModal.ChallengeContestCourseSurvey}
          onOk={() => {
            updateQueryParams({
              challengeSignUpSuccess: undefined,
              challengeId: undefined,
            })
            setModal(EModal.None)
          }}
          challengeId={challengeId}
        />
      )}
      <MembershipQuestionnaireModal
        isOpen={modal === EModal.MembershipQuestionnaire}
        onOk={() => {
          updateQueryParams({ signUpSuccess: undefined })
          setModal(EModal.None)
        }}
        onRequestClose={() => {
          updateQueryParams({ signUpSuccess: undefined })
          setModal(EModal.None)
        }}
      />
    </>
  )
}

const WebUserSetting_query = graphql(
  `
    query WebUserSetting_query {
      currentUser {
        __typename
        ... on User {
          id
          notCompletedTutorials {
            kind
          }
          gotPaymentProblem
          isChallengeUserOnly
          hasSubscription
          hasCurriculumLicense
          ...BasicInfo_viewer
          ...PaymentInfo_viewer
          ...PlanInfo_viewer
          ...CurriculumLicenseList_viewer
          ...CurriculumPlayerList_viewer
          ...AddEmptyPlayerButton_viewer
        }
      }
      activeChallenges {
        id
        publishedNow
        registrationAvailable
        hasChallengePlayers
        ...ChallengeBanner_challenge
      }
    }
  `,
  [
    ChallengeBanner_challenge,
    BasicInfo_viewer,
    PaymentInfo_viewer,
    PlanInfo_viewer,
    CurriculumLicenseList_viewer,
    CurriculumPlayerList_viewer,
    AddEmptyPlayerButton_viewer,
  ]
)
